import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

interface AnnouncerApi {
  /** Polite status message (WCAG 4.1.3): saved, deleted, status changed, page changed, … */
  announce: (message: string) => void;
  /** Assertive message for errors that need immediate attention. */
  alert: (message: string) => void;
}

const AnnouncerContext = createContext<AnnouncerApi>({ announce: () => undefined, alert: () => undefined });

/** Two visually hidden live regions shared by the whole app. */
export function AnnouncerProvider({ children }: { children: ReactNode }) {
  const [polite, setPolite] = useState('');
  const [assertive, setAssertive] = useState('');
  const toggle = useRef(false);
  const announce = useCallback((message: string) => {
    // Alternate a trailing no-break space so that repeating the same message is announced again.
    toggle.current = !toggle.current;
    setPolite(message + (toggle.current ? ' ' : ''));
  }, []);
  const alert = useCallback((message: string) => {
    toggle.current = !toggle.current;
    setAssertive(message + (toggle.current ? ' ' : ''));
  }, []);
  const api = useMemo(() => ({ announce, alert }), [announce, alert]);
  return (
    <AnnouncerContext.Provider value={api}>
      {children}
      <div className="govuk-visually-hidden" aria-live="polite" aria-atomic="true" data-testid="live-polite">
        {polite}
      </div>
      <div className="govuk-visually-hidden" aria-live="assertive" aria-atomic="true" data-testid="live-assertive">
        {assertive}
      </div>
    </AnnouncerContext.Provider>
  );
}

export function useAnnouncer(): AnnouncerApi {
  return useContext(AnnouncerContext);
}
